<template>
    <div id="notes-form" class="notes-form-class">
        <fg-input :label="textBoxLabel" class="mb-0">
            <b-form-textarea v-model="note.body" name="taNote" block-listener :placeholder="placeholder"
                :autofocus="!!parentNote" />
        </fg-input>
        <div class="d-flex align-items-center actions">
            <div class="flex-fill">
                <div class="d-flex">
                    <b-button variant="success" size="sm" name="btnAddNote" @click="save(undefined)"
                        :disabled="disableSaveButton" block-listener>
                        <i :class="{ 'mr-1': true, 'far fa-sticky-note': !parentNote, 'fa fa-reply': parentNote }" />
                        {{ saveButtonLabel }}
                    </b-button>
                    <div v-if="!parentNote" class="ml-2 d-flex flex-fill">
                        <b-dropdown class="activity-dropdown" variant="primary" dropup size="sm">
                            <template #button-content>
                                <small>Add Activity</small>
                            </template>
                            <b-dropdown-item @click="save(NoteActivityType.PhoneCall)">
                                <i class="fa fa-phone mr-1" />
                                Add Phone Call
                            </b-dropdown-item>
                            <b-dropdown-item @click="save(NoteActivityType.TextMessage)">
                                <i class="fa fa-comment-dots mr-1" />
                                Add Text Message
                            </b-dropdown-item>
                            <b-dropdown-item @click="save(NoteActivityType.Electronic)">
                                <i class="fa fa-at mr-1" />
                                Add Electronic
                            </b-dropdown-item>
                            <b-dropdown-item @click="save(NoteActivityType.Portal)">
                                <i class="fa fa-wifi mr-1" />
                                Add Portal
                            </b-dropdown-item>
                            <b-dropdown-item @click="save(NoteActivityType.Fax)">
                                <i class="fa fa-fax mr-1" />
                                Add Fax
                            </b-dropdown-item>
                        </b-dropdown>
                        <div class="activity-buttons">
                            <b-button size="sm" variant="primary" @click="save(NoteActivityType.PhoneCall)">
                                <i class="fa fa-phone mr-1" />
                                <small>Add <span v-if="!templates.length" class="extra">Phone</span> Call</small>
                            </b-button>
                            <b-button size="sm" variant="primary" @click="save(NoteActivityType.TextMessage)">
                                <i class="fa fa-comment-dots mr-1" />
                                <small>Add Text <span v-if="!templates.length" class="extra">Message</span></small>
                            </b-button>
                            <b-button size="sm" variant="primary" @click="save(NoteActivityType.Electronic)">
                                <i class="fa fa-at mr-1" />
                                <small>Add Electronic</small>
                            </b-button>
                            <b-button size="sm" variant="primary" @click="save(NoteActivityType.Portal)">
                                <i class="fa fa-wifi mr-1" />
                                <small>Add Portal</small>
                            </b-button>
                            <b-button size="sm" variant="primary" @click="save(NoteActivityType.Fax)">
                                <i class="fa fa-fax mr-1" />
                                <small>Add Fax</small>
                            </b-button>
                        </div>
                        <b-dropdown v-if="templates.length" id="template-dropdown" variant="secondary" size="sm" right
                            dropup>
                            <template #button-content>
                                <small>Templates</small>
                            </template>
                            <b-dropdown-item v-for="template in templates" :key="template" :value="template"
                                @click="note.body = template">
                                {{ template }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                    <b-button v-if="parentNote" size="sm" variant="link" @click="$emit('cancel')">
                        Cancel
                    </b-button>
                </div>
            </div>
            <div v-if="!parentNote">
                <b-form-checkbox v-model="private" switch v-if="showPharmacistOnlyOption">
                    <small>Pharmacist Only</small>

                    <HelpPopup placement="bottomleft">
                        When enabled, only pharmacists and administrators will be able to view the contents of this
                        note.
                    </HelpPopup>
                </b-form-checkbox>
            </div>
        </div>
    </div>
</template>

<script lang="ts">

import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Note, NoteActivityType, NoteVisibility } from '@/models/Note';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Table, TableColumn } from "element-ui";
import axios, { AxiosError } from 'axios';
import { EntityType } from "@/models/EntityType";
import HelpPopup from "@/components/HelpPopup.vue";

@Component({
    name: "NotesForm",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        HelpPopup
    }
})
export default class NotesForm extends Vue {
    @Prop({ default: 0 }) private entityId!: number;
    @Prop({ default: 0 }) private entityType!: EntityType;
    @Prop({ default: true }) private bodyRequired!: boolean;

    /**
     * @prefixNote: String that will be inserted at the beginning of the note and saved with it.
     */
    @Prop({ default: '' }) private prefixNote!: string;
    @Prop({ default: "Add Note" }) private saveButtonLabel!: string;
    @Prop({ default: "Write a note..." }) private placeholder!: string;
    @Prop() protected textBoxLabel!: string;
    @Prop({ default: false }) silent!: boolean;
    @Prop({ default: null }) private parentNote!: Note | null;
    @Prop({ default: () => [] }) protected templates!: string[];
    @Prop({ default: false }) protected emitsOnly!: boolean;

    private private = false;

    private note: Note = { body: "" } as Note;

    NoteActivityType = NoteActivityType;

    mounted() {
        this.fetchPharmacistOnlyNotesConfig();
    }

    async save(activityType?: NoteActivityType) {
        if (this.prefixNote)
            this.note.body = `${this.prefixNote} ${this.note.body || ''}`;

        if ((activityType === 0 || !!activityType) && !this.note.body?.trim()) {
            this.note.body = " ";
        }

        if (!this.note.body) {
            this.$emit("saved");
            return;
        }

        this.note.entityType = this.entityType;
        this.note.entityId = this.entityId.toString();
        this.note.visibility = this.private ? NoteVisibility.Pharmacist : NoteVisibility.AllUsers;
        this.note.activityType = activityType;

        if (this.parentNote) {
            this.note.parentNoteId = this.parentNote.id as number;
        }

        if (this.emitsOnly) {
            this.$emit("note", { ...this.note, createdBy: this.$user.userName, created: new Date() });
            this.note = { body: "" } as Note;
            return;
        }

        try {
            const response = await axios.post(`Note`, this.note);

            this.$emit("saved", response.data)
            if (!this.silent) this.$notification(NotificationOptions.successSaveNotificationPreset("Note"));
            this.note = { body: "" } as Note;
        } catch (error) {
            console.error("Error while posting new note.", { error, response: (error as AxiosError)?.response });
            this.$notification(NotificationOptions.error(error));
        }
    }

    get hasActivityType() {
        return this.note.activityType === 0 || !!this.note.activityType;
    }

    get disableSaveButton(): boolean {
        if (this.hasActivityType) return false;
        return (this.bodyRequired && !this.note?.body);
    }

    @Watch("note.body")
    noteChanged(val: string) {
        this.$emit('input', val);
    }

    protected supportsPharmacistOnlyNotes = false;

    get showPharmacistOnlyOption() {
        return this.supportsPharmacistOnlyNotes && this.$permissions.some(p => p.groupName.toLowerCase() === "pharmacists");
    }

    async fetchPharmacistOnlyNotesConfig() {
        try {
            const response = await this.$http.get<boolean>("/Configuration/PharmacistOnlyNotes");
            this.supportsPharmacistOnlyNotes = response.data;
        } catch {
            // ignore
        }
    }

}
</script>

<style scoped>
.notes-form-class {
    container-type: inline-size;
    container-name: note-container;
}

.extra {
    display: none;
}

.actions {
    flex-direction: column;
}

.activity-buttons {
    display: none;
}

::v-deep(.dropdown-menu) {
    min-width: 350px;
}

::v-deep(.dropdown-item) {
    white-space: normal;
    padding: 6px 8px !important;
    line-height: 1.35;
}

@container note-container (min-width: 800px) {
    .extra {
        display: inline;
    }

    .actions {
        flex-direction: row;
    }

    .activity-dropdown {
        display: none;
    }

    .activity-buttons {
        display: block;
    }
}
</style>
